/**
 * CalculatorService – mirrors Angular calculator.service.ts.
 * Processes grid edit events, runs KPI formulas, and calculates article indices.
 */
import type { CalculatorConfig } from '../models';
import {
  Quarter, Period, Week,
  KPI_AI, KPI_SP,
} from '../constants';
import { getGridRowDataKeys } from './gridDataService';

// ── Module-level maps ─────────────────────────────────────────────

const levelCalculatorMap = new Map<string, CalculatorConfig[]>();
const levelGroundCalculatorMap = new Map<string, CalculatorConfig[]>();

// ── Public API ────────────────────────────────────────────────────

export function initializeCalculators(config: CalculatorConfig[]): void {
  levelCalculatorMap.set(Week, config.filter((c) => c.level === Week));
  levelCalculatorMap.set(Period, config.filter((c) => c.level === Period));
  levelCalculatorMap.set(Quarter, config.filter((c) => c.level === Quarter));
}

export function initializeGroundCalculators(config: CalculatorConfig[]): void {
  levelGroundCalculatorMap.set(Period, config.filter((c) => c.level === Period));
  levelGroundCalculatorMap.set(Quarter, config.filter((c) => c.level === Quarter));
}

/**
 * Main entry-point when cells are edited (or on initial load).
 * Returns fresh row-data array from the updated gridStateSnapshot.
 */
export function processGridEditEvents(
  fieldKeys: { field: string; kpi: string }[],
  gridStateSnapshot: Map<string, Record<string, unknown>>,
  isInitialLoad: boolean,
): Record<string, unknown>[] {
  executeKpiCalculators(Week, fieldKeys, gridStateSnapshot, isInitialLoad);
  executeKpiCalculators(Period, fieldKeys, gridStateSnapshot, isInitialLoad);
  executeKpiCalculators(Quarter, fieldKeys, gridStateSnapshot, isInitialLoad);
  executeArticleIndexCalculations(gridStateSnapshot);
  return [...gridStateSnapshot.values()];
}

// ── Internal: KPI calculators ─────────────────────────────────────

function executeKpiCalculators(
  level: string,
  fieldKeysInfo: { field: string; kpi: string }[],
  gridStateSnapshot: Map<string, Record<string, unknown>>,
  isInitialLoad: boolean,
): void {
  const planCalcs = levelCalculatorMap.get(level)?.filter((c) => !c.kpiKey.startsWith(KPI_AI)) ?? [];
  const groundCalcs = levelGroundCalculatorMap.get(level);
  const uniqueChangePeriodKpiMap = new Map<string, string[]>();
  const groupingLength = getGroupingLength(level);
  const groupingConstant = getGroupingConstant(level);
  const gridKeys = getGridRowDataKeys();

  for (const info of fieldKeysInfo) {
    const fieldWithoutEs = level === Week ? info.field : info.field.replace('_es', '');
    const periodOfWeek = fieldWithoutEs.substring(0, fieldWithoutEs.length - groupingLength);
    const existing = uniqueChangePeriodKpiMap.get(periodOfWeek);
    if (existing) existing.push(info.kpi);
    else uniqueChangePeriodKpiMap.set(periodOfWeek, [info.kpi]);
  }

  uniqueChangePeriodKpiMap.forEach((value, key) => {
    for (let i = 0; i < planCalcs.length; i++) {
      if (!isInitialLoad && !planCalcs[i].dependentKpiKeys.some((dep) => value.includes(dep))) continue;

      const configKpiKey = planCalcs[i].kpiKey;
      const planResult = calculatePlanResultData(planCalcs[i], value, key, gridStateSnapshot, gridKeys);
      const groundResult = calculateGroundResultData(groundCalcs?.[i], key, gridStateSnapshot, gridKeys);

      const targetRow = gridStateSnapshot.get(configKpiKey);
      if (!targetRow) continue;

      if (planResult && planResult !== 0) {
        targetRow[key + groupingConstant] = planResult + getGroundResultWhenPlanIsAvailable(groundResult);
      } else {
        targetRow[key + groupingConstant] = getGroundResultWhenPlanIsZero(groundResult);
      }
    }
  });
}

// ── Internal: Article Index ───────────────────────────────────────

function executeArticleIndexCalculations(gridStateSnapshot: Map<string, Record<string, unknown>>): void {
  const aiConfigs = levelCalculatorMap.get(Period)?.filter((c) => c.kpiKey.startsWith(KPI_AI)) ?? [];
  const uniquePeriodMap = new Map<string, boolean>();
  const groupingLength = getGroupingLength(Period);
  const groupingConstant = getGroupingConstant(Period);
  const gridKeys = getGridRowDataKeys();

  for (const field of gridKeys) {
    uniquePeriodMap.set(field.substring(0, field.length - groupingLength), true);
  }

  for (const [key] of uniquePeriodMap) {
    for (const cfg of aiConfigs) {
      if (cfg.kpiKey) calculateArticleIndex(cfg, key, gridStateSnapshot, groupingConstant, gridKeys);
    }
  }
}

function calculateArticleIndex(
  cfg: CalculatorConfig,
  key: string,
  gridStateSnapshot: Map<string, Record<string, unknown>>,
  groupingConstant: string,
  gridKeys: string[],
): void {
  const weeksInPeriod = gridKeys.filter((f) => f.includes(key));
  const sumPeriodRowData = findSumPeriodRowData(cfg, gridStateSnapshot);
  const { planData, groundData } = extractPlanAndGroundData(sumPeriodRowData, weeksInPeriod);
  const articleIndexResult = cfg.formula([planData, groundData]);
  const displayValue = formatArticleIndexResult(articleIndexResult, planData, groundData);
  setArticleIndexResult(cfg, key, groupingConstant, displayValue, gridStateSnapshot);
}

// ── Helpers ───────────────────────────────────────────────────────

function findSumPeriodRowData(cfg: CalculatorConfig, snap: Map<string, Record<string, unknown>>): Record<string, unknown> | null {
  const mc = cfg.marketCode;
  if (mc) {
    const row = snap.get(`${KPI_SP}__${mc}`);
    if (row) return row;
  }
  for (const [k, v] of snap.entries()) {
    if (k.startsWith(`${KPI_SP}__`)) return v;
  }
  return snap.get(KPI_SP) ?? null;
}

function extractPlanAndGroundData(
  sumRow: Record<string, unknown> | null,
  weeksInPeriod: string[],
): { planData: number[]; groundData: number[] } {
  const planData: number[] = [];
  const groundData: number[] = [];
  if (sumRow) {
    for (const w of weeksInPeriod) {
      const parsed = parsePlanAndGroundValue(sumRow[w]);
      planData.push(parsed.plan);
      groundData.push(parsed.ground);
    }
  } else {
    planData.push(...weeksInPeriod.map(() => 0));
    groundData.push(...weeksInPeriod.map(() => 0));
  }
  return { planData, groundData };
}

function formatArticleIndexResult(result: number, planData: number[], groundData: number[]): string {
  const planHas = planData.some((v) => v > 0);
  const groundHas = groundData.some((v) => v > 0);
  if (!planHas && !groundHas) return '';
  if (result === -1 || !planData.length || !groundData.length) return '-';
  if (planHas && groundHas) return result > 5 ? Math.round(result).toString() : result.toFixed(3);
  if (planHas || groundHas) return '-';
  return '';
}

function setArticleIndexResult(
  cfg: CalculatorConfig,
  key: string,
  groupingConstant: string,
  displayValue: string,
  snap: Map<string, Record<string, unknown>>,
): void {
  const mc = cfg.kpiKey?.split('__')?.[1];
  if (mc) {
    const specificRow = snap.get(`${KPI_AI}__${mc}`);
    if (specificRow) { specificRow[key + groupingConstant] = displayValue; return; }
  }
  for (const [k, v] of snap.entries()) {
    if (k.startsWith(`${KPI_AI}__`) && v) { v[key + groupingConstant] = displayValue; break; }
  }
}

function calculatePlanResultData(
  cfg: CalculatorConfig | undefined,
  changedKpis: string[],
  key: string,
  snap: Map<string, Record<string, unknown>>,
  gridKeys: string[],
): number | undefined {
  if (!cfg) return undefined;
  let result: number | undefined;
  for (const dep of cfg.dependentKpiKeys) {
    if (changedKpis.includes(dep)) {
      const data: Array<number[]> = [];
      const weeksInPeriod = gridKeys.filter((f) => f.includes(key));
      for (const kpi of cfg.dependentKpiKeys) {
        const rowData = snap.get(kpi);
        data.push(populateKpiPeriodData(weeksInPeriod, rowData));
      }
      result ??= cfg.formula(data);
    }
  }
  return result;
}

function calculateGroundResultData(
  cfg: CalculatorConfig | undefined,
  key: string,
  snap: Map<string, Record<string, unknown>>,
  gridKeys: string[],
): number | undefined {
  if (!cfg) return undefined;
  let result: number | undefined;
  for (const _ of cfg.dependentKpiKeys) {
    const data: Array<number[]> = [];
    const weeksInPeriod = gridKeys.filter((f) => f.includes(key));
    for (const kpi of cfg.dependentKpiKeys) {
      data.push(populateKpiPeriodData(weeksInPeriod, snap.get(kpi)));
    }
    result ??= cfg.formula(data);
  }
  return result;
}

function populateKpiPeriodData(weeksInPeriod: string[], rowData: Record<string, unknown> | undefined): number[] {
  return weeksInPeriod.map((wk) => parseNumericValue(rowData?.[wk]) || 0);
}

function parseNumericValue(value: unknown): number {
  if (value == null) return 0;
  if (typeof value === 'number') return Number.isNaN(value) ? 0 : value;  
  if (typeof value !== 'string') return 0;
  const str = typeof value === 'string' ? value : String(value);
  const numMatch = /(\d+(?:\.\d+)?)/.exec(str);
  const parsed = numMatch?.[1] ? Number.parseFloat(numMatch[1]) : Number(str);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function parsePlanAndGroundValue(value: unknown): { plan: number; ground: number } {
  if (value == null) return { plan: 0, ground: 0 };
  if (typeof value === 'number') return { plan: Number.isNaN(value) ? 0 : value, ground: 0 };
  if (typeof value !== 'string') return { plan: 0, ground: 0 };
  const str = value.trim();

  const planGroundMatch = /^(\d+(?:\.\d+)?)\s*\((\d+(?:\.\d+)?)\)$/.exec(str);
  if (planGroundMatch) return { plan: Number.parseFloat(planGroundMatch[1]) || 0, ground: Number.parseFloat(planGroundMatch[2]) || 0 };

  const planWithDashMatch = /^(\d+(?:\.\d+)?)\s*\(-\)$/.exec(str);
  if (planWithDashMatch) return { plan: Number.parseFloat(planWithDashMatch[1]) || 0, ground: 0 };

  const groundOnlyMatch = /^\((\d+(?:\.\d+)?)\)$/.exec(str);
  if (groundOnlyMatch) return { plan: 0, ground: Number.parseFloat(groundOnlyMatch[1]) || 0 };

  const planOnlyMatch = /^(\d+(?:\.\d+)?)$/.exec(str);
  if (planOnlyMatch) return { plan: Number.parseFloat(planOnlyMatch[1]) || 0, ground: 0 };

  return { plan: 0, ground: 0 };
}

function getGroundResultWhenPlanIsAvailable(groundResult: number | undefined): string {
  if (groundResult == null) return '';
  if (groundResult === 0) return '(-)';
  return `(${groundResult})`;
}

function getGroundResultWhenPlanIsZero(groundResult: number | undefined): string | number {
  return (groundResult == null || groundResult === 0) ? '' : `(${groundResult})`;
}

function getGroupingConstant(level: string): string {
  if (level === Week) return '';
  if (level === Period) return '_W1';
  return '_P1_W1';
}

function getGroupingLength(level: string): number {
  if (level === Week) return 0;
  if (level === Period) return 3;
  return 6;
}
